import React, { useContext, useEffect, useMemo, useState } from "react";
import MapBoxField from "../../../components/map/MapBoxField";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, VStack, Select, Badge, Button, Input, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, useToast } from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";
import { useTranslation } from 'react-i18next';

export default function AdminResellers() {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;

  const [loading, setLoading] = useState(true);
  const [resellers, setResellers] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);
  const toast = useToast();

  const editDisclosure = useDisclosure();
  const createDisclosure = useDisclosure();
  const [selected, setSelected] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAddress, setEditAddress] = useState("");

  const [createName, setCreateName] = useState("");
  const [createPhone, setCreatePhone] = useState("");
  const [createAddress, setCreateAddress] = useState("");

  // Drag & Drop state
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Build tree from flat list
  const buildResellerTree = (items) => {
    if (!Array.isArray(items)) return [];
    const urlMap = new Map();
    const idMap = new Map();
    // Create node copies and index by url and id
    items.forEach((r) => {
      const copy = { ...r, children: [] };
      urlMap.set(r.url, copy);
      try { idMap.set(urlToId(r.url), copy); } catch (_) {}
    });
    const roots = new Set(items.map((r) => r.url));

    const resolveParentNode = (parent) => {
      if (!parent) return null;
      if (typeof parent === "string") return urlMap.get(parent) || null;
      if (typeof parent === "number") return idMap.get(parent) || null;
      if (typeof parent === "object") {
        const pUrl = parent.url || null;
        if (pUrl) return urlMap.get(pUrl) || null;
        const pid = parent.id ?? (pUrl ? urlToId(pUrl) : null);
        if (pid != null) return idMap.get(pid) || null;
      }
      return null;
    };

    items.forEach((r) => {
      const node = urlMap.get(r.url);
      const parentNode = resolveParentNode(r.parent);
      if (parentNode && parentNode !== node) {
        parentNode.children.push(node);
        roots.delete(r.url);
      }
    });

    // Return roots in stable order
    return Array.from(roots).map((url) => urlMap.get(url));
  };

  const tree = useMemo(() => buildResellerTree(resellers), [resellers]);
  const [expanded, setExpanded] = useState(() => new Map());
  useEffect(() => {
    // Expand all by default
    const all = new Map();
    const collect = (nodes) => {
      nodes.forEach((n) => {
        all.set(n.url, true);
        if (n.children && n.children.length) collect(n.children);
      });
    };
    collect(tree);
    setExpanded(all);
  }, [tree]);

  const findNodeByUrl = (nodes, url) => {
    for (const n of nodes) {
      if (n.url === url) return n;
      const found = n.children && findNodeByUrl(n.children, url);
      if (found) return found;
    }
    return null;
  };

  const isDescendant = (sourceUrl, targetUrl) => {
    if (!sourceUrl || !targetUrl || sourceUrl === targetUrl) return false;
    const sourceNode = findNodeByUrl(tree, sourceUrl);
    if (!sourceNode) return false;
    const stack = [...(sourceNode.children || [])];
    while (stack.length) {
      const n = stack.pop();
      if (n.url === targetUrl) return true;
      if (n.children && n.children.length) stack.push(...n.children);
    }
    return false;
  };

  const flattenTree = (nodes, level = 0) => {
    const rows = [];
    nodes.forEach((n) => {
      rows.push({ node: n, level });
      if (expanded.get(n.url) && n.children && n.children.length) {
        rows.push(...flattenTree(n.children, level + 1));
      }
    });
    return rows;
  };

  const getUrlById = (id) => {
    const r = (resellers || []).find((x) => {
      try { return urlToId(x.url) === id; } catch (_) { return false; }
    });
    return r?.url || null;
  };

  useEffect(() => {
    if (!isSuperUser) return;
    fetchCities();
    fetchResellers(1);
  }, [isSuperUser]);

  const fetchCities = async () => {
    try {
      const { data } = await client.get("/public/cities/");
      setCities(data.results || data);
    } catch (e) {
      // ignore
    }
  };

  const fetchResellers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (cityFilter) params.set("cities", cityFilter);
      if (isActiveFilter !== "all") params.set("is_active", isActiveFilter === "yes" ? "true" : "false");
      const { data } = await client.get(`/resellers/?${params.toString()}`);
      const items = data.results || data;
      setResellers(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (item) => {
    setSelected(item);
    setEditName(item.name || "");
    setEditPhone(item.phone_number || "");
    setEditAddress(item.address || "");
    editDisclosure.onOpen();
  };

  const toggleActive = async (item) => {
    try {
      const id = urlToId(item.url);
      await client.patch(`/resellers/${id}/`, { is_active: !item.is_active });
      toast({ title: item.is_active ? "Bayi devre dışı bırakıldı" : "Bayi aktif edildi", status: "success" });
      fetchResellers(currentPage);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      toast({ title: "İşlem hatası", description: detail, status: "error" });
    }
  };

  const onDragStartRow = (item) => {
    const id = urlToId(item.url);
    setDraggedId(id);
  };

  const onDragOverRow = (item, e) => {
    e.preventDefault();
    const id = urlToId(item.url);
    if (id !== draggedId) setDragOverId(id);
  };

  const onDropRow = async (targetItem, e) => {
    e.preventDefault();
    try {
      const targetId = urlToId(targetItem.url);
      const sourceId = draggedId;
      setDragOverId(null);
      setDraggedId(null);
      if (!sourceId || sourceId === targetId) return;
      // Parent assignment via URL (HyperlinkedModelSerializer expects URL)
      await client.patch(`/resellers/${sourceId}/`, { parent: `/resellers/${targetId}/` });
      toast({ title: "Üst bayi atandı", status: "success" });
      fetchResellers(currentPage);
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || String(e2);
      toast({ title: "Atama hatası", description: detail, status: "error" });
    }
  };

  const onDropToRoot = async (e) => {
    e.preventDefault();
    try {
      const sourceId = draggedId;
      setDragOverId(null);
      setDraggedId(null);
      if (!sourceId) return;
      await client.patch(`/resellers/${sourceId}/`, { parent: null });
      toast({ title: "Köke taşındı", status: "success" });
      fetchResellers(currentPage);
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || String(e2);
      toast({ title: "Taşıma hatası", description: detail, status: "error" });
    }
  };

  const toggleNode = (url) => {
    setExpanded((prev) => new Map(prev).set(url, !prev.get(url)));
  };

  const saveEdit = async () => {
    try {
      await client.patch(`/resellers/${urlToId(selected.url)}/`, {
        name: editName,
        phone_number: editPhone,
        address: editAddress,
      });
      toast({ title: "Güncellendi", status: "success" });
      editDisclosure.onClose();
      fetchResellers(currentPage);
    } catch (e) {
      toast({ title: "Güncelleme hatası", status: "error" });
    }
  };

  const createReseller = async () => {
    try {
      await client.post(`/resellers/`, {
        name: createName,
        phone_number: createPhone,
        address: createAddress,
      });
      toast({ title: "Bayi oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setCreateName(""); setCreatePhone(""); setCreateAddress("");
      fetchResellers(1);
    } catch (e) {
      toast({ title: "Oluşturma hatası", status: "error" });
    }
  };

  const deleteReseller = async (item) => {
    if (!window.confirm(`Silinsin mi? ${item.name}`)) return;
    try {
      await client.delete(`/resellers/${urlToId(item.url)}/`);
      toast({ title: "Silindi", status: "success" });
      fetchResellers(currentPage);
    } catch (e) {
      toast({ title: "Silme hatası", status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>{t('reseller.admin.title')}</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <HStack spacing={3}>
          <Select placeholder={t('reseller.admin.city')} value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} w="220px">
            {cities.map((c) => <option key={c.url || c.name} value={urlToId(c.url)}>{c.name}</option>)}
          </Select>
          <Select value={isActiveFilter} onChange={(e) => setIsActiveFilter(e.target.value)} w="180px">
            <option value="all">{t('common.all')}</option>
            <option value="yes">{t('common.active')}</option>
            <option value="no">{t('common.inactive')}</option>
          </Select>
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchResellers(1); }}>{t('filter')}</Button>
          <Button colorScheme="green" onClick={createDisclosure.onOpen}>{t('reseller.admin.new_reseller')}</Button>
          <ExportButton
            data={(resellers || []).map(r => ({
              [t('reseller.admin.export.columns.id')]: urlToId(r.url),
              [t('reseller.admin.export.columns.name')]: r.name || "",
              [t('reseller.admin.export.columns.cities')]: (r.cities || []).map(c => c.name).join(", ") || '-',
              [t('reseller.admin.export.columns.open_ticket')]: r.nof_open_tickets ?? 0,
              [t('reseller.admin.export.columns.managers')]: (r.managers || []).map(m => `${m.first_name} ${m.last_name}`).join(", ") || '-',
              [t('reseller.admin.export.columns.status')]: r.is_active ? t('common.active') : t('common.inactive'),
            }))}
            filename={t('reseller.admin.export.filename')}
            columns={[
              { key: t('reseller.admin.export.columns.id'), label: t('reseller.admin.export.columns.id') },
              { key: t('reseller.admin.export.columns.name'), label: t('reseller.admin.export.columns.name') },
              { key: t('reseller.admin.export.columns.cities'), label: t('reseller.admin.export.columns.cities') },
              { key: t('reseller.admin.export.columns.open_ticket'), label: t('reseller.admin.export.columns.open_ticket') },
              { key: t('reseller.admin.export.columns.managers'), label: t('reseller.admin.export.columns.managers') },
              { key: t('reseller.admin.export.columns.status'), label: t('reseller.admin.export.columns.status') },
            ]}
            sheetName={t('reseller.admin.export.sheet_name')}
            excelOptions={{ title: t('reseller.admin.export.sheet_name') }}
          />
        </HStack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        <Box>
          <Box
            p={3}
            mb={3}
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="gray.300"
            borderRadius="md"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropToRoot}
            title={t('reseller.admin.move_to_root_hint')}
          >
            {t('reseller.admin.move_to_root_label')}
          </Box>

          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>{t('reseller.admin.table.name')}</Th>
                <Th>{t('reseller.admin.table.cities')}</Th>
                <Th>{t('reseller.admin.table.open_ticket')}</Th>
                <Th>{t('reseller.admin.table.managers')}</Th>
                <Th>{t('reseller.admin.table.status')}</Th>
                <Th>{t('actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {flattenTree(tree).map(({ node, level }) => (
                <Tr
                  key={node.url}
                  draggable
                  onDragStart={() => onDragStartRow(node)}
                  onDragOver={(e) => onDragOverRow(node, e)}
                  onDrop={(e) => {
                    const sourceUrl = getUrlById(draggedId);
                    if (!sourceUrl || isDescendant(sourceUrl, node.url) || sourceUrl === node.url) {
                      setDragOverId(null);
                      setDraggedId(null);
                      e.preventDefault();
                      return;
                    }
                    onDropRow(node, e);
                  }}
                  style={{ backgroundColor: dragOverId === urlToId(node.url) ? "#ebf8ff" : undefined }}
                  title={t('reseller.admin.drag_assign_hint')}
                >
                  <Td>
                    <HStack spacing={2}>
                      <Button size="xs" variant="ghost" onClick={() => toggleNode(node.url)}>
                        {node.children && node.children.length ? (expanded.get(node.url) ? "−" : "+") : "·"}
                      </Button>
                      <Box pl={level * 6}>
                        {node.name}
                      </Box>
                    </HStack>
                  </Td>
                  <Td>{(node.cities || []).map((c) => c.name).join(", ") || '-'}</Td>
                  <Td><Badge colorScheme="orange">{node.nof_open_tickets ?? 0}</Badge></Td>
                  <Td>{(node.managers || []).map((m) => `${m.first_name} ${m.last_name}`).join(", ") || '-'}</Td>
                  <Td>
                    <Badge colorScheme={node.is_active ? "green" : "gray"}>{node.is_active ? t('common.active') : t('common.inactive')}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Button size="xs" onClick={() => onEdit(node)}>{t('edit')}</Button>
                      <Button size="xs" onClick={() => toggleActive(node)}>{node.is_active ? t('reseller.admin.deactivate') : t('reseller.admin.activate')}</Button>
                      <Button size="xs" colorScheme="red" onClick={() => deleteReseller(node)}>{t('delete')}</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <Box mt={4}>
        <Pagination path={"admin/bayi"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} onPageChange={(p) => fetchResellers(p)} />
      </Box>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('reseller.admin.edit_reseller')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel>{t('name')}</FormLabel>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('phone')}</FormLabel>
                <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('address')}</FormLabel>
                <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
                <MapBoxField value={editAddress} onChangeAddress={setEditAddress} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={editDisclosure.onClose}>{t('cancel')}</Button>
            <Button colorScheme="blue" onClick={saveEdit}>{t('save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('reseller.admin.new_reseller')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel>{t('name')}</FormLabel>
                <Input value={createName} onChange={(e) => setCreateName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('phone')}</FormLabel>
                <Input value={createPhone} onChange={(e) => setCreatePhone(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('address')}</FormLabel>
                <Input value={createAddress} onChange={(e) => setCreateAddress(e.target.value)} />
                <MapBoxField value={createAddress} onChangeAddress={setCreateAddress} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={createDisclosure.onClose}>{t('cancel')}</Button>
            <Button colorScheme="green" onClick={createReseller}>{t('reseller.admin.create')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}